/**
 * useTypedSignature Hook
 *
 * A reusable React hook for typed signature generation.
 * Renders text as a signature using stylized fonts and converts to PNG.
 *
 * Features:
 * - Multiple handwriting font options
 * - Automatic font loading from Google Fonts
 * - High-DPI canvas rendering
 * - Debounced rendering for performance
 * - Font fallback handling
 *
 * @example
 * ```tsx
 * const typedSignature = useTypedSignature({
 *   initialName: user.fullName,
 *   defaultFont: 'Dancing Script'
 * });
 *
 * return (
 *   <input
 *     value={typedSignature.name}
 *     onChange={(e) => typedSignature.setName(e.target.value)}
 *   />
 * );
 * ```
 */

import { useState, useRef, useEffect, useCallback } from 'react';

export interface SignatureFont {
  name: string;
  display: string;
  style: string;
}

export const SIGNATURE_FONTS: SignatureFont[] = [
  { name: 'Dancing Script', display: 'Dancing Script (Modern)', style: 'font-family: "Dancing Script", cursive' },
  { name: 'Allura', display: 'Allura (Elegant)', style: 'font-family: "Allura", cursive' },
  { name: 'Great Vibes', display: 'Great Vibes (Formal)', style: 'font-family: "Great Vibes", cursive' },
  { name: 'Pacifico', display: 'Pacifico (Casual)', style: 'font-family: "Pacifico", cursive' },
  { name: 'Satisfy', display: 'Satisfy (Contemporary)', style: 'font-family: "Satisfy", cursive' },
  { name: 'Tangerine', display: 'Tangerine (Thin)', style: 'font-family: "Tangerine", cursive' }
];

export interface TypedSignatureOptions {
  initialName?: string;
  defaultFont?: string;
  fontSize?: number;
  canvasWidth?: number;
  canvasHeight?: number;
  debounceMs?: number;
  onGenerate?: (dataUrl: string, font: string) => void;
  onError?: (error: Error) => void;
}

export interface UseTypedSignatureReturn {
  // Canvas reference
  canvasRef: React.RefObject<HTMLCanvasElement>;

  // State
  name: string;
  selectedFont: string;
  signatureDataUrl: string;
  isGenerating: boolean;
  fontError: boolean;
  availableFonts: SignatureFont[];

  // Methods
  setName: (name: string) => void;
  setSelectedFont: (font: string) => void;
  clear: () => void;
  regenerate: () => void;
  getDataUrl: () => string | null;
  isValid: () => boolean;
}

const DEFAULT_OPTIONS: Required<TypedSignatureOptions> = {
  initialName: '',
  defaultFont: 'Dancing Script',
  fontSize: 64,
  canvasWidth: 800,  // Increased from 600 to accommodate longer names
  canvasHeight: 200,
  debounceMs: 300,
  onGenerate: () => {},
  onError: () => {}
};

export function useTypedSignature(options: TypedSignatureOptions = {}): UseTypedSignatureReturn {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
  const {
    initialName,
    defaultFont,
    fontSize,
    canvasWidth,
    canvasHeight,
    debounceMs,
    onGenerate,
    onError
  } = mergedOptions;

  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTimeoutRef = useRef<NodeJS.Timeout>();
  const fontLinkRef = useRef<HTMLLinkElement | null>(null);

  // State
  const [name, setName] = useState(initialName);
  const [selectedFont, setSelectedFont] = useState(defaultFont);
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [fontError, setFontError] = useState(false);

  // Load Google Fonts on mount
  useEffect(() => {
    const fontFamilies = SIGNATURE_FONTS
      .map(f => `${f.name.replace(' ', '+')  }:wght@400;700`)
      .join('&family=');

    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    fontLinkRef.current = link;

    return () => {
      if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
        document.head.removeChild(fontLinkRef.current);
      }
    };
  }, []);

  // Render signature
  const renderSignature = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !name.trim()) {
      setSignatureDataUrl('');
      setIsGenerating(false);
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      const error = new Error('Failed to get canvas context');
      onError(error);
      return;
    }

    setIsGenerating(true);
    setFontError(false);

    try {
      // Load the font
      try {
        await document.fonts.load(`${fontSize}px "${selectedFont}"`, name);
      } catch (fontLoadError) {
        setFontError(true);
        // Continue with fallback
      }

      // Wait for font to be applied
      await new Promise(resolve => setTimeout(resolve, 100));

      // Set up high-DPI rendering
      const dpr = window.devicePixelRatio || 1;

      // Set canvas dimensions
      canvas.width = canvasWidth * dpr;
      canvas.height = canvasHeight * dpr;
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;

      ctx.scale(dpr, dpr);

      // Clear canvas with white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Draw signature text
      ctx.font = `${fontSize}px "${selectedFont}", cursive`;
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.fillText(name, canvasWidth / 2, canvasHeight / 2);

      // Generate data URL
      const dataUrl = canvas.toDataURL('image/png');
      setSignatureDataUrl(dataUrl);
      setIsGenerating(false);

      onGenerate(dataUrl, selectedFont);
    } catch (error) {
      const err = error as Error;
      setIsGenerating(false);
      setFontError(true);
      onError(err);
    }
  }, [name, selectedFont, fontSize, canvasWidth, canvasHeight, onGenerate, onError]);

  // Debounced rendering
  useEffect(() => {
    // Clear existing timeout
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }

    // Set new timeout
    renderTimeoutRef.current = setTimeout(() => {
      renderSignature();
    }, debounceMs);

    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
    };
  }, [name, selectedFont, debounceMs, renderSignature]);

  // Methods
  const clear = useCallback(() => {
    setName('');
    setSignatureDataUrl('');
    setIsGenerating(false);
    setFontError(false);
  }, []);

  const regenerate = useCallback(() => {
    renderSignature();
  }, [renderSignature]);

  const getDataUrl = useCallback((): string | null => {
    return signatureDataUrl || null;
  }, [signatureDataUrl]);

  const isValid = useCallback((): boolean => {
    return !!name.trim() && !!signatureDataUrl && !fontError;
  }, [name, signatureDataUrl, fontError]);

  return {
    // Canvas reference
    canvasRef,

    // State
    name,
    selectedFont,
    signatureDataUrl,
    isGenerating,
    fontError,
    availableFonts: SIGNATURE_FONTS,

    // Methods
    setName,
    setSelectedFont,
    clear,
    regenerate,
    getDataUrl,
    isValid
  };
}